import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Grid, Chip, LinearProgress, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  IconButton, Alert, CircularProgress, Tabs, Tab, Card, CardContent, Stack,
} from '@mui/material';
import {
  ArrowBack, Edit, Add, CheckCircle, Agriculture, PlayArrow, Pause, Delete, AttachMoney,
  AutoAwesome, TrendingUp, Payments, ReceiptLong, RequestPage, Link as LinkIcon, Chat, Send,
  DriveFileMove,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  farmOperationService, staffService, contractorService, activityLogService, buyerService, aiAdvisorService,
  reportService, paymentService, expenseService, paymentRequestService, intercropLinkService, projectService,
} from '@smartgrow/shared';
import type { OperationTask, TaskType, OperationType, OperationStatus, AssigneeType, ActivityLog, FarmOperation, YieldUnit } from '@smartgrow/shared';

const fmt = (n?: number) => n == null ? '—' : `₦${n.toLocaleString('en-NG')}`;

const TASK_STATUS_COLORS: Record<string, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  SCHEDULED: 'default',
  IN_PROGRESS: 'info',
  COMPLETED: 'success',
  SKIPPED: 'warning',
  CANCELLED: 'error',
};

const TASK_TYPES_BY_OP: Record<OperationType, TaskType[]> = {
  SEASONAL_CROP: ['LAND_CLEARING', 'PLOUGHING', 'RIDGING', 'PLANTING', 'TRANSPLANTING',
    'FERTILIZER_APPLICATION', 'HERBICIDE_APPLICATION', 'PESTICIDE_APPLICATION', 'WEEDING',
    'IRRIGATION', 'HARVESTING', 'PROCESSING', 'TRANSPORTATION', 'OTHER'],
  PERENNIAL_CROP: ['LAND_CLEARING', 'PLANTING', 'TRANSPLANTING', 'FERTILIZER_APPLICATION',
    'HERBICIDE_APPLICATION', 'PESTICIDE_APPLICATION', 'WEEDING', 'IRRIGATION', 'PRUNING',
    'HARVESTING', 'MAINTENANCE', 'PROCESSING', 'TRANSPORTATION', 'OTHER'],
  LIVESTOCK: ['POND_PREPARATION', 'STOCKING', 'FEEDING', 'VACCINATION', 'WATER_QUALITY_CHECK',
    'PESTICIDE_APPLICATION', 'IRRIGATION', 'FENCING', 'PROCESSING', 'TRANSPORTATION',
    'MAINTENANCE', 'OTHER'],
  GENERAL: ['LAND_CLEARING', 'MAINTENANCE', 'FENCING', 'CONSTRUCTION', 'STAFF_TRAINING',
    'TRANSPORTATION', 'OTHER'],
};

const TASK_LABELS: Record<TaskType, string> = {
  LAND_CLEARING: 'Land Clearing', PLOUGHING: 'Ploughing', RIDGING: 'Ridging',
  PLANTING: 'Planting', TRANSPLANTING: 'Transplanting',
  FERTILIZER_APPLICATION: 'Fertilizer Application', HERBICIDE_APPLICATION: 'Herbicide Application',
  PESTICIDE_APPLICATION: 'Pesticide Application', WEEDING: 'Weeding', IRRIGATION: 'Irrigation',
  PRUNING: 'Pruning', HARVESTING: 'Harvesting', STOCKING: 'Stocking', FEEDING: 'Feeding',
  VACCINATION: 'Vaccination', WATER_QUALITY_CHECK: 'Water Quality Check',
  POND_PREPARATION: 'Pond/Enclosure Prep', PROCESSING: 'Processing/Packaging',
  TRANSPORTATION: 'Transportation', MAINTENANCE: 'Maintenance', FENCING: 'Fencing',
  CONSTRUCTION: 'Construction', STAFF_TRAINING: 'Staff Training', OTHER: 'Other',
};

const QUANTITY_ACTIVITY_TYPES = new Set<TaskType>([
  'PLANTING', 'TRANSPLANTING', 'FERTILIZER_APPLICATION', 'HERBICIDE_APPLICATION',
  'PESTICIDE_APPLICATION', 'IRRIGATION', 'STOCKING', 'FEEDING', 'VACCINATION',
  'PROCESSING', 'HARVESTING',
]);

// Rain isn't a task — it's a weather observation, so it's kept out of the TaskType enum (which
// carries cost-category/assignee semantics tasks need) and instead handled as a special-cased
// activityType value. The backend's activityType column is a free string, so "RAIN" round-trips
// fine; the AI advisor reads these entries to factor rain risk into its cost/yield estimates.
const RAIN_ACTIVITY_TYPE = 'RAIN';
const RAIN_INTENSITY_OPTIONS = ['LIGHT', 'MODERATE', 'HEAVY'] as const;
type ActivityTypeValue = TaskType | typeof RAIN_ACTIVITY_TYPE;

// Human-readable labels for the AI price-suggestion prompt — "Unit: BAG_100KG" reads awkwardly
// to the model compared to "Unit: Bags (100kg)", so map the raw enum before sending it.
const YIELD_UNIT_LABELS: Record<YieldUnit, string> = {
  KG: 'Kilograms (kg)',
  BAG_50KG: 'Bags (50kg)',
  BAG_100KG: 'Bags (100kg)',
  TON: 'Tonnes',
  UNIT: 'Units (piece count)',
};

export const FarmOperationDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const opId = id!;

  const [tab, setTab] = useState(0);
  const [taskDialog, setTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState<OperationTask | null>(null);
  const [harvestDialog, setHarvestDialog] = useState(false);
  const [editingHarvestId, setEditingHarvestId] = useState<string | null>(null);
  const [completeDialog, setCompleteDialog] = useState<OperationTask | null>(null);
  const [editCostDialog, setEditCostDialog] = useState<OperationTask | null>(null);
  const [editCostValue, setEditCostValue] = useState('');
  const [taskForm, setTaskForm] = useState({ taskType: 'OTHER' as TaskType, title: '', scheduledDate: '', estimatedCost: '', assigneeType: '' as AssigneeType | '', assignedStaffId: '', assignedContractorId: '' });
  const [harvestForm, setHarvestForm] = useState({ harvestDate: '', quantityHarvested: '', unit: 'kg', pricePerUnit: '', revenueAmount: '', buyerId: '', notes: '' });
  const [newBuyerName, setNewBuyerName] = useState('');
  const [completeForm, setCompleteForm] = useState({ actualCost: '', notes: '' });
  const [activityDialog, setActivityDialog] = useState(false);
  const [editingActivityId, setEditingActivityId] = useState<string | null>(null);
  const [activityForm, setActivityForm] = useState({
    activityDate: '',
    activityType: 'PLANTING' as ActivityTypeValue,
    quantityUsed: '',
    quantityUnit: 'kg',
    rainIntensity: 'MODERATE' as typeof RAIN_INTENSITY_OPTIONS[number],
    remarks: '',
    recordedByName: '',
  });
  const [error, setError] = useState('');
  const [moveDialog, setMoveDialog] = useState(false);
  const [moveTargetProjectId, setMoveTargetProjectId] = useState('');
  const [moveTaskTarget, setMoveTaskTarget] = useState<OperationTask | null>(null);
  const [moveTaskProjectId, setMoveTaskProjectId] = useState('');
  const [moveTaskOperationId, setMoveTaskOperationId] = useState('');
  const [intercropDialogOpen, setIntercropDialogOpen] = useState(false);
  const [intercropSearch, setIntercropSearch] = useState('');
  const [intercropTargetId, setIntercropTargetId] = useState('');
  const [intercropSharedAcres, setIntercropSharedAcres] = useState('');
  const [intercropNote, setIntercropNote] = useState('');

  const { data: op, isLoading } = useQuery({
    queryKey: ['farm-operation', opId],
    queryFn: () => farmOperationService.getById(opId),
  });

  const { data: tasks = [] } = useQuery({
    queryKey: ['farm-tasks', opId],
    queryFn: () => farmOperationService.getTasksByOperation(opId),
    enabled: !!opId,
  });

  const { data: harvests = [] } = useQuery({
    queryKey: ['farm-harvests', opId],
    queryFn: () => farmOperationService.getHarvestRecords(opId),
    enabled: !!opId,
  });

  const { data: activityLogs = [] } = useQuery({
    queryKey: ['activity-logs', opId],
    queryFn: () => activityLogService.getByOperation(opId),
    enabled: !!opId,
  });

  const { data: staffData } = useQuery({
    queryKey: ['staff-all'],
    queryFn: () => staffService.getStaff({ size: 200 }),
    enabled: taskDialog,
  });

  const { data: contractorsData } = useQuery({
    queryKey: ['contractors-active'],
    queryFn: () => contractorService.getContractors({ size: 100 }),
    enabled: taskDialog,
  });

  const { data: buyers = [] } = useQuery({
    queryKey: ['buyers'],
    queryFn: () => buyerService.getAll(),
    enabled: harvestDialog,
  });

  const staffList = staffData?.content ?? [];
  const contractorList = contractorsData?.content ?? [];

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['farm-operation', opId] });
    qc.invalidateQueries({ queryKey: ['farm-tasks', opId] });
    qc.invalidateQueries({ queryKey: ['farm-harvests', opId] });
    qc.invalidateQueries({ queryKey: ['activity-logs', opId] });
  };

  const updateStatusMutation = useMutation({
    mutationFn: (status: OperationStatus) => farmOperationService.updateStatus(opId, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['farm-operation', opId] }),
    onError: () => setError('Failed to update operation status.'),
  });

  const { data: projectsForMove } = useQuery({
    queryKey: ['projects-for-filter'],
    queryFn: () => projectService.getProjects({ size: 100 }),
    enabled: moveDialog,
  });

  const moveOperationMutation = useMutation({
    mutationFn: () => farmOperationService.moveToProject(opId, moveTargetProjectId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['farm-operation', opId] });
      setMoveDialog(false);
      setMoveTargetProjectId('');
      setError('');
    },
    onError: () => setError('Failed to move operation.'),
  });

  const { data: projectsForTaskMove } = useQuery({
    queryKey: ['projects-for-filter'],
    queryFn: () => projectService.getProjects({ size: 100 }),
    enabled: !!moveTaskTarget,
  });

  const { data: operationsForTaskMove } = useQuery({
    queryKey: ['operations-for-filter', moveTaskProjectId],
    queryFn: () => farmOperationService.getByProject(moveTaskProjectId),
    enabled: !!moveTaskTarget && !!moveTaskProjectId,
  });

  const moveTaskMutation = useMutation({
    mutationFn: () => farmOperationService.moveTask(moveTaskTarget!.id, moveTaskOperationId),
    onSuccess: () => {
      invalidate();
      setMoveTaskTarget(null);
      setMoveTaskProjectId('');
      setMoveTaskOperationId('');
      setError('');
    },
    onError: () => setError('Failed to move task.'),
  });

  const addTaskMutation = useMutation({
    mutationFn: farmOperationService.createTask,
    onSuccess: () => { invalidate(); setTaskDialog(false); setEditingTask(null); setError(''); },
    onError: () => setError('Failed to add task.'),
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({ taskId, data }: { taskId: string; data: Parameters<typeof farmOperationService.updateTask>[1] }) =>
      farmOperationService.updateTask(taskId, data),
    onSuccess: () => { invalidate(); setTaskDialog(false); setEditingTask(null); setError(''); },
    onError: () => setError('Failed to update task.'),
  });

  const completeTaskMutation = useMutation({
    mutationFn: ({ taskId, cost, notes }: { taskId: string; cost: number; notes?: string }) =>
      farmOperationService.completeTask(taskId, cost, notes),
    onSuccess: () => { invalidate(); setCompleteDialog(null); setError(''); },
    onError: () => setError('Failed to complete task.'),
  });

  const editCostMutation = useMutation({
    mutationFn: ({ taskId, actualCost }: { taskId: string; actualCost: number }) =>
      farmOperationService.updateTaskActualCost(taskId, actualCost),
    onSuccess: () => { invalidate(); setEditCostDialog(null); setEditCostValue(''); setError(''); },
    onError: () => setError('Failed to update actual cost.'),
  });

  const addHarvestMutation = useMutation({
    mutationFn: farmOperationService.createHarvestRecord,
    onSuccess: () => { invalidate(); setHarvestDialog(false); setEditingHarvestId(null); setError(''); },
    onError: () => setError('Failed to record harvest.'),
  });

  const updateHarvestMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof farmOperationService.updateHarvestRecord>[1] }) =>
      farmOperationService.updateHarvestRecord(id, data),
    onSuccess: () => { invalidate(); setHarvestDialog(false); setEditingHarvestId(null); setError(''); },
    onError: () => setError('Failed to update harvest.'),
  });

  const createBuyerMutation = useMutation({
    mutationFn: buyerService.create,
    onSuccess: (buyer) => {
      qc.invalidateQueries({ queryKey: ['buyers'] });
      setHarvestForm((f) => ({ ...f, buyerId: buyer.id }));
      setNewBuyerName('');
    },
    onError: () => setError('Failed to add buyer.'),
  });

  const deleteHarvestMutation = useMutation({
    mutationFn: farmOperationService.deleteHarvestRecord,
    onSuccess: () => invalidate(),
    onError: () => setError('Failed to delete harvest.'),
  });

  const addActivityMutation = useMutation({
    mutationFn: activityLogService.create,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['activity-logs', opId] }); setActivityDialog(false); setEditingActivityId(null); setError(''); },
    onError: () => setError('Failed to save activity log.'),
  });

  const updateActivityMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof activityLogService.update>[1] }) =>
      activityLogService.update(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['activity-logs', opId] }); setActivityDialog(false); setEditingActivityId(null); setError(''); },
    onError: () => setError('Failed to save activity log.'),
  });

  const deleteActivityMutation = useMutation({
    mutationFn: activityLogService.remove,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['activity-logs', opId] }),
    onError: () => setError('Failed to delete activity log.'),
  });

  const { data: intercropLinks = [] } = useQuery({
    queryKey: ['intercrop-links-operation', opId],
    queryFn: () => intercropLinkService.getByOperation(opId),
  });

  const { data: intercropSearchData } = useQuery({
    queryKey: ['farm-operations-search-intercrop', intercropSearch],
    queryFn: () => farmOperationService.search({ search: intercropSearch, size: 10 }),
    enabled: intercropDialogOpen,
  });
  const intercropCandidates = (intercropSearchData?.content ?? []).filter((o) => o.id !== opId);

  const createIntercropMutation = useMutation({
    mutationFn: () => {
      const target = intercropCandidates.find((o) => o.id === intercropTargetId);
      if (!target || !op) throw new Error('Missing operation');
      return intercropLinkService.create({
        projectAId: op.projectId,
        projectBId: target.projectId,
        hostOperationId: op.id,
        intercropOperationId: target.id,
        sharedAcres: intercropSharedAcres ? Number(intercropSharedAcres) : undefined,
        note: intercropNote || undefined,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['intercrop-links-operation', opId] });
      setIntercropDialogOpen(false);
      setIntercropTargetId('');
      setIntercropSearch('');
      setIntercropSharedAcres('');
      setIntercropNote('');
    },
    onError: () => setError('Failed to link operation.'),
  });

  const deleteIntercropMutation = useMutation({
    mutationFn: (linkId: string) => intercropLinkService.delete(linkId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['intercrop-links-operation', opId] }),
    onError: () => setError('Failed to remove intercrop link.'),
  });

  const userData = JSON.parse(localStorage.getItem('user_data') ?? '{}');
  const isAdmin = userData?.role === 'ADMIN' || userData?.role === 'OWNER';

  if (isLoading) return <Box sx={{ p: 4 }}><LinearProgress /></Box>;
  if (!op) return <Box sx={{ p: 4 }}><Alert severity="error">Operation not found.</Alert></Box>;

  const pct = op.totalTasks > 0 ? Math.round((op.completedTasks / op.totalTasks) * 100) : 0;
  const availableTaskTypes = TASK_TYPES_BY_OP[op.operationType] ?? ['OTHER'];

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>Back</Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight="bold">{op.name || op.projectName}</Typography>
          <Typography
            variant="body2" color="primary"
            sx={{ cursor: 'pointer', display: 'inline-block', '&:hover': { textDecoration: 'underline' } }}
            onClick={() => navigate(`/dashboard/projects/${op.projectId}`)}
          >
            {op.projectName}
          </Typography>
        </Box>
        <Chip label={op.operationType.replace('_', ' ')} color="primary" />
        <Chip label={op.status} color={op.status === 'ACTIVE' ? 'success' : op.status === 'COMPLETED' ? 'default' : op.status === 'PAUSED' ? 'warning' : 'default'} />
        {isAdmin && (
          <Button startIcon={<DriveFileMove />} variant="outlined" onClick={() => setMoveDialog(true)}>
            Move to Project
          </Button>
        )}
        {op.status === 'ACTIVE' && (
          <Button
            startIcon={<Pause />} variant="outlined" color="warning"
            disabled={updateStatusMutation.isPending}
            onClick={() => updateStatusMutation.mutate('PAUSED')}
          >
            Pause
          </Button>
        )}
        {op.status === 'PAUSED' && (
          <Button
            startIcon={<PlayArrow />} variant="outlined" color="success"
            disabled={updateStatusMutation.isPending}
            onClick={() => updateStatusMutation.mutate('ACTIVE')}
          >
            Resume
          </Button>
        )}
        {(op.status === 'ACTIVE' || op.status === 'PAUSED' || op.status === 'PLANNING') && (
          <Button
            startIcon={<CheckCircle />} variant="outlined" color="primary"
            disabled={updateStatusMutation.isPending}
            onClick={() => updateStatusMutation.mutate('COMPLETED')}
          >
            Complete
          </Button>
        )}
        {op.status === 'PLANNING' && (
          <Button
            startIcon={<PlayArrow />} variant="outlined" color="success"
            disabled={updateStatusMutation.isPending}
            onClick={() => updateStatusMutation.mutate('ACTIVE')}
          >
            Activate
          </Button>
        )}
        <Button startIcon={<Edit />} variant="outlined" onClick={() => navigate(`/dashboard/farm/${op.id}/edit`)}>
          Edit
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Overview" />
        <Tab label="Financials" />
      </Tabs>

      {tab === 1 && <OperationFinancialsTab operationId={opId} />}

      {tab === 0 && (
      <>
      {/* Summary */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Details</Typography>
            <Grid container spacing={2}>
              {op.season && <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Season</Typography><Typography>{op.season}</Typography></Grid>}
              {op.landAreaAcres && <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Land Area</Typography><Typography>{Number(op.landAreaAcres).toLocaleString('en-NG')} acres</Typography></Grid>}
              {op.cropVariety && <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Crop</Typography><Typography>{op.cropVariety}</Typography></Grid>}
              {op.animalType && <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Animal</Typography><Typography>{op.animalType}</Typography></Grid>}
              {op.animalQuantity && <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Quantity</Typography><Typography>{op.animalQuantity.toLocaleString()}</Typography></Grid>}
            </Grid>
            {op.notes && <><Divider sx={{ my: 2 }} /><Typography variant="body2" color="text.secondary">{op.notes}</Typography></>}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>Task Progress</Typography>
            <Typography variant="h3" fontWeight="bold" color={pct === 100 ? 'success.main' : 'primary.main'}>{pct}%</Typography>
            <LinearProgress variant="determinate" value={pct} color={pct === 100 ? 'success' : 'primary'} sx={{ my: 1, height: 8, borderRadius: 4 }} />
            <Typography variant="body2" color="text.secondary">{op.completedTasks} of {op.totalTasks} tasks completed</Typography>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Actual Cost</Typography>
                <Typography fontWeight="medium">{fmt(op.totalActualCost)}</Typography>
                <Typography variant="caption" color="text.secondary" display="block">tasks + expenses + payments</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary">Est. Cost</Typography>
                <Typography fontWeight="medium">{fmt((op.totalEstimatedCost ?? 0) + (op.totalFinancialsCost ?? 0))}</Typography>
                <Typography variant="caption" color="text.secondary" display="block">task estimates + expenses + payments</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {(op.expectedYieldKg != null || op.expectedYieldQuantity != null || op.expectedPricePerUnit != null
        || op.expectedRevenue != null || op.expectedFinalCost != null || op.expectedNetRevenue != null) && (() => {
        // op.expectedFinalCost is the AI's own total cost estimate (cultivation cost — which
        // already starts from the operation's recorded task cost — plus harvesting/bagging/
        // transportation). It must NOT be stacked on top of totalEstimatedCost/totalFinancialsCost
        // here: that would double-count the task/financials cost the AI's cultivationCost figure
        // already folds in, inflating Final Cost and dragging Net Revenue negative. Show it as-is,
        // matching how the AI itself computed netRevenue = revenue - finalCost.
        const displayedFinalCost = op.expectedFinalCost;
        return (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Estimates</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Last AI estimate applied to this operation — yield, price and cost figures update whenever a new estimate is applied.
          </Typography>
          <Grid container spacing={2}>
            {op.expectedYieldQuantity != null && op.yieldUnit != null && op.yieldUnit !== 'KG' && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Estimated Yield ({YIELD_UNIT_LABELS[op.yieldUnit]})</Typography>
                <Typography>{Number(op.expectedYieldQuantity).toLocaleString('en-NG')}</Typography>
              </Grid>
            )}
            {op.expectedYieldKg != null && <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Estimated Yield</Typography><Typography>{Number(op.expectedYieldKg).toLocaleString('en-NG')} kg</Typography></Grid>}
            {op.expectedPricePerUnit != null && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Estimated Price{op.yieldUnit ? ` (per ${YIELD_UNIT_LABELS[op.yieldUnit]})` : ''}</Typography>
                <Typography>{fmt(op.expectedPricePerUnit)}</Typography>
              </Grid>
            )}
            {op.expectedRevenue != null && <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Estimated Revenue</Typography><Typography>{fmt(op.expectedRevenue)}</Typography></Grid>}
            {op.expectedFinalCost != null && <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Estimated Final Cost</Typography><Typography>{fmt(displayedFinalCost!)}</Typography></Grid>}
            {op.expectedRevenue != null && displayedFinalCost != null && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Estimated Net Revenue</Typography>
                <Typography color={op.expectedRevenue - displayedFinalCost >= 0 ? 'success.main' : 'error.main'} fontWeight="bold">
                  {fmt(op.expectedRevenue - displayedFinalCost)}
                </Typography>
              </Grid>
            )}
          </Grid>
          {(op.expectedHarvestingCost != null || op.expectedBaggingCost != null || op.expectedTransportationCost != null) && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Estimated cost breakdown</Typography>
              <Grid container spacing={2}>
                {op.expectedHarvestingCost != null && <Grid item xs={6} sm={4}><Typography variant="caption" color="text.secondary">Harvesting</Typography><Typography variant="body2">{fmt(op.expectedHarvestingCost)}</Typography></Grid>}
                {op.expectedBaggingCost != null && <Grid item xs={6} sm={4}><Typography variant="caption" color="text.secondary">Bagging</Typography><Typography variant="body2">{fmt(op.expectedBaggingCost)}</Typography></Grid>}
                {op.expectedTransportationCost != null && <Grid item xs={6} sm={4}><Typography variant="caption" color="text.secondary">Transportation</Typography><Typography variant="body2">{fmt(op.expectedTransportationCost)}</Typography></Grid>}
              </Grid>
            </>
          )}
        </Paper>
        );
      })()}

      {op.cropVariety && <YieldEstimateCard operation={op} onApplied={invalidate} />}

      {op.cropVariety && <SalesPlannerCard operation={op} />}

      {/* Tasks */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Tasks ({tasks.length})</Typography>
          <Button startIcon={<Add />} variant="contained" size="small" onClick={() => { setEditingTask(null); setTaskForm({ taskType: availableTaskTypes[0], title: TASK_LABELS[availableTaskTypes[0]], scheduledDate: '', estimatedCost: '', assigneeType: '', assignedStaffId: '', assignedContractorId: '' }); setTaskDialog(true); }}>
            Add Task
          </Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell>Task</TableCell>
                <TableCell>Assignee</TableCell>
                <TableCell>Scheduled</TableCell>
                <TableCell>Est. Cost</TableCell>
                <TableCell>Actual Cost</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(tasks).length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ color: 'text.secondary', py: 3 }}>No tasks yet</TableCell></TableRow>
              )}
              {(tasks).map((t) => (
                <TableRow key={t.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">{t.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{TASK_LABELS[t.taskType]}</Typography>
                  </TableCell>
                  <TableCell>
                    {t.assignedStaffName
                      ? <Typography variant="caption">{t.assignedStaffName}</Typography>
                      : t.assignedContractorName
                        ? <Typography variant="caption">{t.assignedContractorName}</Typography>
                        : t.assigneeType
                          ? <Typography variant="caption" color="text.secondary">{t.assigneeType}</Typography>
                          : '—'}
                  </TableCell>
                  <TableCell>{t.scheduledDate ?? '—'}</TableCell>
                  <TableCell>{fmt(t.estimatedCost)}</TableCell>
                  <TableCell>{fmt(t.actualCost)}</TableCell>
                  <TableCell><Chip label={t.status} color={TASK_STATUS_COLORS[t.status]} size="small" /></TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <IconButton size="small" title="Edit task" onClick={() => {
                      setEditingTask(t);
                      setTaskForm({
                        taskType: t.taskType,
                        title: t.title,
                        scheduledDate: t.scheduledDate ?? '',
                        estimatedCost: t.estimatedCost != null ? String(t.estimatedCost) : '',
                        assigneeType: t.assigneeType ?? '',
                        assignedStaffId: t.assignedStaffId ?? '',
                        assignedContractorId: t.assignedContractorId ?? '',
                      });
                      setTaskDialog(true);
                    }}>
                      <Edit fontSize="small" />
                    </IconButton>
                    {t.status === 'SCHEDULED' || t.status === 'IN_PROGRESS' ? (
                      <IconButton size="small" color="success" title="Mark complete" onClick={() => { setCompleteForm({ actualCost: t.estimatedCost != null ? String(t.estimatedCost) : '', notes: '' }); setCompleteDialog(t); }}>
                        <CheckCircle fontSize="small" />
                      </IconButton>
                    ) : null}
                    {isAdmin && t.status === 'COMPLETED' && (
                      <IconButton size="small" color="warning" title="Edit actual cost" onClick={() => { setEditCostValue(t.actualCost != null ? String(t.actualCost) : ''); setEditCostDialog(t); }}>
                        <AttachMoney fontSize="small" />
                      </IconButton>
                    )}
                    {isAdmin && (
                      <IconButton size="small" title="Move to another operation" onClick={() => {
                        setMoveTaskProjectId(op.projectId);
                        setMoveTaskOperationId('');
                        setMoveTaskTarget(t);
                      }}>
                        <DriveFileMove fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            {tasks.length > 0 && (
              <TableFooter>
                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                  <TableCell colSpan={3}><Typography variant="body2" fontWeight="medium">Total</Typography></TableCell>
                  <TableCell><Typography variant="body2" fontWeight="medium">{fmt(tasks.reduce((sum, t) => sum + (t.estimatedCost ?? 0), 0))}</Typography></TableCell>
                  <TableCell><Typography variant="body2" fontWeight="medium">{fmt(tasks.reduce((sum, t) => sum + (t.actualCost ?? 0), 0))}</Typography></TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Paper>

      {/* Activity Log */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Activity Log ({(activityLogs as ActivityLog[]).length})</Typography>
          <Button startIcon={<Add />} variant="contained" size="small"
            onClick={() => {
              setEditingActivityId(null);
              setActivityForm({ activityDate: '', activityType: availableTaskTypes[0], quantityUsed: '', quantityUnit: 'kg', rainIntensity: 'MODERATE', remarks: '', recordedByName: '' });
              setActivityDialog(true);
            }}>
            Add Entry
          </Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell>Date</TableCell>
                <TableCell>Activity</TableCell>
                <TableCell>Qty Used</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>Remarks</TableCell>
                <TableCell>Recorded By</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {(activityLogs as ActivityLog[]).length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ color: 'text.secondary', py: 3 }}>No activity entries yet</TableCell></TableRow>
              )}
              {(activityLogs as ActivityLog[]).map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>{log.activityDate}</TableCell>
                  <TableCell>{log.activityType === RAIN_ACTIVITY_TYPE ? 'Rain' : TASK_LABELS[log.activityType as TaskType] ?? log.activityType}</TableCell>
                  <TableCell>{log.activityType === RAIN_ACTIVITY_TYPE ? '—' : log.quantityUsed ?? '—'}</TableCell>
                  <TableCell>{log.activityType === RAIN_ACTIVITY_TYPE ? (log.quantityUnit ? `${log.quantityUnit} intensity` : '—') : log.quantityUnit ?? '—'}</TableCell>
                  <TableCell>{log.remarks ?? '—'}</TableCell>
                  <TableCell>{log.recordedByName ?? '—'}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <IconButton size="small" onClick={() => {
                      setEditingActivityId(log.id);
                      const isRain = log.activityType === RAIN_ACTIVITY_TYPE;
                      setActivityForm({
                        activityDate: log.activityDate,
                        activityType: isRain ? RAIN_ACTIVITY_TYPE : log.activityType as TaskType,
                        quantityUsed: isRain ? '' : log.quantityUsed != null ? String(log.quantityUsed) : '',
                        quantityUnit: isRain ? 'kg' : log.quantityUnit ?? 'kg',
                        rainIntensity: isRain && log.quantityUnit
                          ? (log.quantityUnit as typeof RAIN_INTENSITY_OPTIONS[number])
                          : 'MODERATE',
                        remarks: log.remarks ?? '',
                        recordedByName: log.recordedByName ?? '',
                      });
                      setActivityDialog(true);
                    }}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error"
                      disabled={deleteActivityMutation.isPending}
                      onClick={() => { if (window.confirm('Delete this activity entry?')) deleteActivityMutation.mutate(log.id); }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Harvest Records */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Harvest Records ({(harvests).length})</Typography>
          <Button startIcon={<Add />} variant="contained" size="small" color="success"
            onClick={() => { setEditingHarvestId(null); setHarvestForm({ harvestDate: '', quantityHarvested: '', unit: 'kg', pricePerUnit: '', revenueAmount: '', buyerId: '', notes: '' }); setHarvestDialog(true); }}>
            Record Harvest
          </Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell>Date</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Price/Unit</TableCell>
                <TableCell>Revenue</TableCell>
                <TableCell>Buyer</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {(harvests).length === 0 && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 3 }}>No harvest records yet</TableCell></TableRow>
              )}
              {(harvests).map((h) => (
                <TableRow key={h.id} hover>
                  <TableCell>{h.harvestDate}</TableCell>
                  <TableCell>{h.quantityHarvested} {h.unit}</TableCell>
                  <TableCell>{fmt(h.pricePerUnit)}</TableCell>
                  <TableCell>{fmt(h.revenueAmount)}</TableCell>
                  <TableCell>{h.buyerDisplayName ?? h.buyerName ?? '—'}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <IconButton size="small" onClick={() => {
                      setEditingHarvestId(h.id);
                      setHarvestForm({
                        harvestDate: h.harvestDate,
                        quantityHarvested: String(h.quantityHarvested),
                        unit: h.unit ?? 'kg',
                        pricePerUnit: h.pricePerUnit != null ? String(h.pricePerUnit) : '',
                        revenueAmount: h.revenueAmount != null ? String(h.revenueAmount) : '',
                        buyerId: h.buyerId ?? '',
                        notes: h.notes ?? '',
                      });
                      setHarvestDialog(true);
                    }}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error"
                      disabled={deleteHarvestMutation.isPending}
                      onClick={() => { if (window.confirm('Delete this harvest record?')) deleteHarvestMutation.mutate(h.id); }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {(harvests).length > 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="right"><strong>Total Revenue</strong></TableCell>
                  <TableCell><strong>{fmt((harvests).reduce((s, h) => s + (h.revenueAmount ?? 0), 0))}</strong></TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Intercropping */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Intercropped With ({intercropLinks.length})</Typography>
          <Button startIcon={<LinkIcon />} variant="outlined" size="small" onClick={() => setIntercropDialogOpen(true)}>
            Link Operation
          </Button>
        </Box>
        {intercropLinks.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No intercrop links. Link this operation with another that shares physical land (e.g. a groundnut crop planted within this cashew field).
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {intercropLinks.map((link) => {
              const isHost = link.hostOperationId === opId;
              const otherName = isHost ? link.intercropOperationName : link.hostOperationName;
              const otherAcres = isHost ? link.intercropOperationAcres : link.hostOperationAcres;
              return (
                <Box key={link.id} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">{otherName ?? `${link.projectAName} ↔ ${link.projectBName}`}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {otherAcres != null && `${otherAcres} acres`}
                      {link.sharedAcres != null && ` · ${link.sharedAcres} acres shared`}
                      {link.note && ` · ${link.note}`}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => deleteIntercropMutation.mutate(link.id)} disabled={deleteIntercropMutation.isPending}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>
      </>
      )}

      {/* Add / Edit Task Dialog */}
      <Dialog open={taskDialog} onClose={() => { setTaskDialog(false); setEditingTask(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTask ? 'Edit Task' : 'Add Task'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField select fullWidth label="Task Type" value={taskForm.taskType}
                onChange={(e) => setTaskForm((f) => ({ ...f, taskType: e.target.value as TaskType, title: TASK_LABELS[e.target.value as TaskType] }))}>
                {availableTaskTypes.map((tt) => <MenuItem key={tt} value={tt}>{TASK_LABELS[tt]}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required label="Title" value={taskForm.title}
                onChange={(e) => setTaskForm((f) => ({ ...f, title: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label="Scheduled Date" value={taskForm.scheduledDate}
                onChange={(e) => setTaskForm((f) => ({ ...f, scheduledDate: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="Estimated Cost (₦)" value={taskForm.estimatedCost}
                onChange={(e) => setTaskForm((f) => ({ ...f, estimatedCost: e.target.value }))}
                inputProps={{ min: 0 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Assignee Type" value={taskForm.assigneeType}
                onChange={(e) => setTaskForm((f) => ({ ...f, assigneeType: e.target.value as AssigneeType | '', assignedStaffId: '', assignedContractorId: '' }))}>
                <MenuItem value="">— None —</MenuItem>
                <MenuItem value="STAFF">Staff</MenuItem>
                <MenuItem value="CONTRACTOR">Contractor</MenuItem>
                <MenuItem value="EXTERNAL">External</MenuItem>
              </TextField>
            </Grid>
            {taskForm.assigneeType === 'STAFF' && (
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth label="Assign to Staff" value={taskForm.assignedStaffId}
                  onChange={(e) => setTaskForm((f) => ({ ...f, assignedStaffId: e.target.value }))}>
                  <MenuItem value="">— Select staff —</MenuItem>
                  {staffList.map((s: any) => <MenuItem key={s.id} value={s.id}>{s.fullName}</MenuItem>)}
                </TextField>
              </Grid>
            )}
            {taskForm.assigneeType === 'CONTRACTOR' && (
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth label="Assign to Contractor" value={taskForm.assignedContractorId}
                  onChange={(e) => setTaskForm((f) => ({ ...f, assignedContractorId: e.target.value }))}>
                  <MenuItem value="">— Select contractor —</MenuItem>
                  {contractorList.map((c: any) => <MenuItem key={c.id} value={c.id}>{c.companyName}</MenuItem>)}
                </TextField>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setTaskDialog(false); setEditingTask(null); }}>Cancel</Button>
          <Button variant="contained"
            disabled={(editingTask ? updateTaskMutation.isPending : addTaskMutation.isPending) || !taskForm.title}
            onClick={() => {
              const payload = {
                farmOperationId: opId,
                taskType: taskForm.taskType,
                title: taskForm.title,
                scheduledDate: taskForm.scheduledDate || undefined,
                estimatedCost: taskForm.estimatedCost ? Number.parseFloat(taskForm.estimatedCost) : undefined,
                assigneeType: taskForm.assigneeType || undefined,
                assignedStaffId: taskForm.assignedStaffId || undefined,
                assignedContractorId: taskForm.assignedContractorId || undefined,
              };
              if (editingTask) {
                updateTaskMutation.mutate({ taskId: editingTask.id, data: payload });
              } else {
                addTaskMutation.mutate(payload);
              }
            }}>
            {(editingTask ? updateTaskMutation.isPending : addTaskMutation.isPending)
              ? <CircularProgress size={18} />
              : editingTask ? 'Save Changes' : 'Add Task'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Task Dialog */}
      <Dialog open={!!completeDialog} onClose={() => setCompleteDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Complete: {completeDialog?.title}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField fullWidth type="number" label="Actual Cost (₦)" value={completeForm.actualCost}
                onChange={(e) => setCompleteForm((f) => ({ ...f, actualCost: e.target.value }))}
                inputProps={{ min: 0 }}
                helperText={completeDialog?.estimatedCost != null ? `Estimated: ${fmt(completeDialog.estimatedCost)}` : 'Leave blank to use estimated cost'} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Completion Notes" value={completeForm.notes}
                onChange={(e) => setCompleteForm((f) => ({ ...f, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteDialog(null)}>Cancel</Button>
          <Button variant="contained" color="success" disabled={completeTaskMutation.isPending}
            startIcon={<CheckCircle />}
            onClick={() => {
              const cost = completeForm.actualCost
                ? Number.parseFloat(completeForm.actualCost)
                : completeDialog!.estimatedCost ?? 0;
              completeTaskMutation.mutate({ taskId: completeDialog!.id, cost, notes: completeForm.notes || undefined });
            }}>
            {completeTaskMutation.isPending ? <CircularProgress size={18} /> : 'Mark Complete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Record / Edit Harvest Dialog */}
      <Dialog open={harvestDialog} onClose={() => { setHarvestDialog(false); setEditingHarvestId(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Agriculture color="success" />
            {editingHarvestId ? 'Edit Harvest Record' : 'Record Harvest'}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required type="date" label="Harvest Date" value={harvestForm.harvestDate}
                onChange={(e) => setHarvestForm((f) => ({ ...f, harvestDate: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={8} sm={5}>
              <TextField fullWidth required type="number" label="Quantity" value={harvestForm.quantityHarvested}
                onChange={(e) => setHarvestForm((f) => ({ ...f, quantityHarvested: e.target.value }))}
                inputProps={{ min: 0, step: 0.1 }} />
            </Grid>
            <Grid item xs={4} sm={3}>
              <TextField select fullWidth label="Unit" value={harvestForm.unit}
                onChange={(e) => setHarvestForm((f) => ({ ...f, unit: e.target.value }))}>
                {['kg', 'litres', 'pieces', 'bags', 'tonnes'].map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="Price per Unit (₦)" value={harvestForm.pricePerUnit}
                onChange={(e) => setHarvestForm((f) => ({ ...f, pricePerUnit: e.target.value }))}
                inputProps={{ min: 0 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="Total Revenue (₦)" value={harvestForm.revenueAmount}
                onChange={(e) => setHarvestForm((f) => ({ ...f, revenueAmount: e.target.value }))}
                inputProps={{ min: 0 }} />
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextField select fullWidth label="Buyer" value={harvestForm.buyerId}
                onChange={(e) => setHarvestForm((f) => ({ ...f, buyerId: e.target.value }))}>
                <MenuItem value="">— None —</MenuItem>
                {buyers.map((b) => <MenuItem key={b.id} value={b.id}>{b.name}{b.contact ? ` (${b.contact})` : ''}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={8} sm={4}>
              <TextField fullWidth label="Or add new buyer" value={newBuyerName}
                onChange={(e) => setNewBuyerName(e.target.value)} />
            </Grid>
            <Grid item xs={4} sm={1}>
              <Button fullWidth sx={{ height: '100%' }} disabled={!newBuyerName.trim() || createBuyerMutation.isPending}
                onClick={() => createBuyerMutation.mutate({ name: newBuyerName.trim() })}>
                Add
              </Button>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Notes" value={harvestForm.notes}
                onChange={(e) => setHarvestForm((f) => ({ ...f, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setHarvestDialog(false); setEditingHarvestId(null); }}>Cancel</Button>
          <Button variant="contained" color="success"
            disabled={(editingHarvestId ? updateHarvestMutation.isPending : addHarvestMutation.isPending) || !harvestForm.harvestDate || !harvestForm.quantityHarvested}
            onClick={() => {
              const payload = {
                farmOperationId: opId,
                harvestDate: harvestForm.harvestDate,
                quantityHarvested: Number.parseFloat(harvestForm.quantityHarvested),
                unit: harvestForm.unit,
                pricePerUnit: harvestForm.pricePerUnit ? Number.parseFloat(harvestForm.pricePerUnit) : undefined,
                revenueAmount: harvestForm.revenueAmount ? Number.parseFloat(harvestForm.revenueAmount) : undefined,
                buyerId: harvestForm.buyerId || undefined,
                notes: harvestForm.notes || undefined,
              };
              if (editingHarvestId) {
                updateHarvestMutation.mutate({ id: editingHarvestId, data: payload });
              } else {
                addHarvestMutation.mutate(payload);
              }
            }}>
            {(editingHarvestId ? updateHarvestMutation.isPending : addHarvestMutation.isPending)
              ? <CircularProgress size={18} />
              : editingHarvestId ? 'Save Changes' : 'Record Harvest'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Actual Cost Dialog (admin only) */}
      <Dialog open={!!editCostDialog} onClose={() => { setEditCostDialog(null); setEditCostValue(''); }} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Actual Cost</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{editCostDialog?.title}</Typography>
          <TextField fullWidth required type="number" label="Actual Cost (₦)" value={editCostValue}
            onChange={(e) => setEditCostValue(e.target.value)}
            inputProps={{ min: 0 }}
            autoFocus />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditCostDialog(null); setEditCostValue(''); }}>Cancel</Button>
          <Button variant="contained" color="warning"
            disabled={editCostMutation.isPending || !editCostValue || !Number.isFinite(Number(editCostValue)) || Number(editCostValue) < 0}
            onClick={() => editCostMutation.mutate({ taskId: editCostDialog!.id, actualCost: Number(editCostValue) })}>
            {editCostMutation.isPending ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add / Edit Activity Log Dialog */}
      <Dialog open={activityDialog} onClose={() => { setActivityDialog(false); setEditingActivityId(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingActivityId ? 'Edit Activity Entry' : 'Add Activity Entry'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required type="date" label="Date" value={activityForm.activityDate}
                onChange={(e) => setActivityForm((f) => ({ ...f, activityDate: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Activity Type" value={activityForm.activityType}
                onChange={(e) => {
                  const type = e.target.value as ActivityTypeValue;
                  setActivityForm((f) => ({
                    ...f,
                    activityType: type,
                    ...(!QUANTITY_ACTIVITY_TYPES.has(type as TaskType) && { quantityUsed: '', quantityUnit: 'kg' }),
                  }));
                }}>
                <MenuItem value={RAIN_ACTIVITY_TYPE}>Rain</MenuItem>
                {availableTaskTypes.map((tt) => <MenuItem key={tt} value={tt}>{TASK_LABELS[tt]}</MenuItem>)}
              </TextField>
            </Grid>
            {activityForm.activityType === RAIN_ACTIVITY_TYPE && (
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth label="Rain Intensity" value={activityForm.rainIntensity}
                  onChange={(e) => setActivityForm((f) => ({ ...f, rainIntensity: e.target.value as typeof RAIN_INTENSITY_OPTIONS[number] }))}>
                  {RAIN_INTENSITY_OPTIONS.map((i) => <MenuItem key={i} value={i}>{i.charAt(0) + i.slice(1).toLowerCase()}</MenuItem>)}
                </TextField>
              </Grid>
            )}
            {QUANTITY_ACTIVITY_TYPES.has(activityForm.activityType as TaskType) && (
              <>
                <Grid item xs={8} sm={6}>
                  <TextField fullWidth type="number" label="Quantity Used" value={activityForm.quantityUsed}
                    onChange={(e) => setActivityForm((f) => ({ ...f, quantityUsed: e.target.value }))}
                    inputProps={{ min: 0, step: 0.001 }} />
                </Grid>
                <Grid item xs={4} sm={6}>
                  <TextField select fullWidth label="Unit" value={activityForm.quantityUnit}
                    onChange={(e) => setActivityForm((f) => ({ ...f, quantityUnit: e.target.value }))}>
                    {['kg', 'bags', 'litres', 'pieces', 'tonnes', 'seedlings'].map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                  </TextField>
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Remarks" value={activityForm.remarks}
                onChange={(e) => setActivityForm((f) => ({ ...f, remarks: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Recorded By" value={activityForm.recordedByName}
                onChange={(e) => setActivityForm((f) => ({ ...f, recordedByName: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setActivityDialog(false); setEditingActivityId(null); }}>Cancel</Button>
          <Button variant="contained"
            disabled={(editingActivityId ? updateActivityMutation.isPending : addActivityMutation.isPending) || !activityForm.activityDate}
            onClick={() => {
              const isRain = activityForm.activityType === RAIN_ACTIVITY_TYPE;
              const payload = {
                farmOperationId: opId,
                activityDate: activityForm.activityDate,
                activityType: activityForm.activityType,
                quantityUsed: isRain ? undefined : activityForm.quantityUsed ? Number.parseFloat(activityForm.quantityUsed) : undefined,
                quantityUnit: isRain ? activityForm.rainIntensity : activityForm.quantityUnit || undefined,
                remarks: activityForm.remarks || undefined,
                recordedByName: activityForm.recordedByName || undefined,
              };
              if (editingActivityId) {
                updateActivityMutation.mutate({ id: editingActivityId, data: payload });
              } else {
                addActivityMutation.mutate(payload);
              }
            }}>
            {(editingActivityId ? updateActivityMutation.isPending : addActivityMutation.isPending)
              ? <CircularProgress size={18} />
              : editingActivityId ? 'Save Changes' : 'Add Entry'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Link Intercrop Operation Dialog */}
      <Dialog open={intercropDialogOpen} onClose={() => setIntercropDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Link Intercropped Operation</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Search operations" value={intercropSearch}
                onChange={(e) => { setIntercropSearch(e.target.value); setIntercropTargetId(''); }}
                placeholder="Search by operation name..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select fullWidth label="Operation" value={intercropTargetId}
                onChange={(e) => setIntercropTargetId(e.target.value)}
                helperText="Pick the companion operation planted within this one's land"
              >
                {intercropCandidates.length === 0 ? (
                  <MenuItem disabled value="">No matching operations</MenuItem>
                ) : (
                  intercropCandidates.map((o) => (
                    <MenuItem key={o.id} value={o.id}>{o.name} — {o.projectName}</MenuItem>
                  ))
                )}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth type="number" label="Shared Acres (optional)" value={intercropSharedAcres}
                onChange={(e) => setIntercropSharedAcres(e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Note (optional)" value={intercropNote}
                onChange={(e) => setIntercropNote(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIntercropDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained" disabled={!intercropTargetId || createIntercropMutation.isPending}
            onClick={() => createIntercropMutation.mutate()}
          >
            {createIntercropMutation.isPending ? <CircularProgress size={18} /> : 'Link'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={moveDialog} onClose={() => setMoveDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Move Operation to Another Project</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Moving "{op.name}" out of <strong>{op.projectName}</strong>. Its tasks, financials, and AI estimates all move with it — only the project link changes.
          </Typography>
          <TextField select fullWidth label="Target Project" value={moveTargetProjectId}
            onChange={(e) => setMoveTargetProjectId(e.target.value)}>
            {(projectsForMove?.content ?? []).filter((p) => p.id !== op.projectId).map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!moveTargetProjectId || moveOperationMutation.isPending}
            onClick={() => moveOperationMutation.mutate()}
          >
            {moveOperationMutation.isPending ? <CircularProgress size={20} /> : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!moveTaskTarget} onClose={() => setMoveTaskTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Move Task</DialogTitle>
        <DialogContent>
          {moveTaskTarget && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Moving "{moveTaskTarget.title}" from <strong>{op.name}</strong> ({op.projectName}).
              Everything about the task — schedule, assignee, cost — carries over unchanged.
            </Typography>
          )}
          <TextField select fullWidth label="Target Project" value={moveTaskProjectId} sx={{ mb: 2 }}
            onChange={(e) => { setMoveTaskProjectId(e.target.value); setMoveTaskOperationId(''); }}>
            {(projectsForTaskMove?.content ?? []).map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </TextField>
          <TextField select fullWidth label="Target Operation" value={moveTaskOperationId} disabled={!moveTaskProjectId}
            onChange={(e) => setMoveTaskOperationId(e.target.value)}>
            {(operationsForTaskMove?.content ?? []).filter((o) => o.id !== opId).length === 0 && (
              <MenuItem value="" disabled>No other operations in this project</MenuItem>
            )}
            {(operationsForTaskMove?.content ?? []).filter((o) => o.id !== opId).map((o) => (
              <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveTaskTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!moveTaskOperationId || moveTaskMutation.isPending}
            onClick={() => moveTaskMutation.mutate()}
          >
            {moveTaskMutation.isPending ? <CircularProgress size={20} /> : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Builds the full update payload the PUT /farm-operations/:id endpoint requires, starting from
// the operation as currently stored and overlaying an AI estimate's numbers on top — same shape
// EditFarmOperationPage used to submit when this card's "Apply" lived on that form. Accepts a
// partial estimate so a price-only "Apply" (no yieldQuantity/costs in the estimate) only touches
// expectedPricePerUnit and the revenue figures it feeds, leaving cost breakdown fields as they
// already were on the operation instead of clobbering them to undefined.
const buildEstimateApplyPayload = (
  operation: FarmOperation,
  estimate: Partial<import('@smartgrow/shared').YieldEstimateResponse>,
) => {
  const payload: import('@smartgrow/shared').CreateFarmOperationRequest = {
    projectId: operation.projectId,
    name: operation.name,
    operationType: operation.operationType,
    season: operation.season,
    seasonInstanceId: operation.seasonInstanceId,
    landAreaAcres: operation.landAreaAcres,
    latitude: operation.latitude,
    longitude: operation.longitude,
    cropVariety: operation.cropVariety,
    animalType: operation.animalType,
    animalQuantity: operation.animalQuantity,
    expectedYieldKg: operation.expectedYieldKg,
    expectedRevenue: operation.expectedRevenue,
    yieldUnit: operation.yieldUnit,
    expectedYieldQuantity: operation.expectedYieldQuantity,
    expectedPricePerUnit: operation.expectedPricePerUnit,
    expectedHarvestingCost: operation.expectedHarvestingCost,
    expectedBaggingCost: operation.expectedBaggingCost,
    expectedTransportationCost: operation.expectedTransportationCost,
    expectedFinalCost: operation.expectedFinalCost,
    expectedNetRevenue: operation.expectedNetRevenue,
    operationBudget: operation.budget,
    notes: operation.notes,
  };
  if (estimate.yieldQuantity != null) payload.expectedYieldQuantity = estimate.yieldQuantity;
  if (estimate.yieldUnit != null) payload.yieldUnit = estimate.yieldUnit;
  if (estimate.pricePerUnit != null) payload.expectedPricePerUnit = estimate.pricePerUnit;
  // A full yield estimate carries its own cost breakdown and should overwrite the stored one
  // (including clearing fields it left blank); a price-only estimate has none of these keys at
  // all, so leave the operation's existing breakdown untouched instead of wiping it to undefined.
  const isFullYieldEstimate = estimate.yieldQuantity !== undefined || estimate.harvestingCost !== undefined
    || estimate.baggingCost !== undefined || estimate.transportationCost !== undefined || estimate.finalCost !== undefined;
  if (isFullYieldEstimate) {
    payload.expectedHarvestingCost = estimate.harvestingCost;
    payload.expectedBaggingCost = estimate.baggingCost;
    payload.expectedTransportationCost = estimate.transportationCost;
    payload.expectedFinalCost = estimate.finalCost;
  }
  const quantity = payload.expectedYieldQuantity;
  const price = payload.expectedPricePerUnit;
  // For a price-only apply, the price was only quoted against operation.yieldUnit when that was
  // already set (see suggestPriceMutation's unitLabel) — with no yieldUnit known, the AI defaults
  // to pricing "per kg", which is not safe to multiply against expectedYieldQuantity if that
  // quantity is actually stored in a different unit (e.g. bags). A full yield estimate always
  // returns its own matching yieldUnit alongside the price, so it's never subject to this guard.
  const unitsAgree = isFullYieldEstimate || operation.yieldUnit != null;
  // Only recompute revenue when both sides of the multiplication are actually known and in
  // agreeing units — falling through to the operation's existing (possibly stale) revenue when
  // quantity is 0/null/undefined, or units can't be confirmed to agree, is intentional: applying
  // just doesn't touch a revenue figure it can't safely recompute, but it also must not silently
  // keep a revenue figure that no longer matches the just-applied price once it CAN be recomputed.
  payload.expectedRevenue = quantity && price && unitsAgree
    ? quantity * price
    : (isFullYieldEstimate ? undefined : payload.expectedRevenue);
  payload.expectedNetRevenue = payload.expectedRevenue != null && payload.expectedFinalCost != null
    ? payload.expectedRevenue - payload.expectedFinalCost
    : (isFullYieldEstimate ? undefined : payload.expectedNetRevenue);
  return payload;
};

// Shared display for any AI price/yield estimate result — a suggest-price result only ever has
// pricePerUnit set, so the other fields naturally render as "—"; a full yield estimate populates
// all of them. Same visual shape either way instead of two different layouts. Typed as a Partial
// of the real YieldEstimateResponse (rather than a hand-rolled structural type) so a field
// rename/type change on the shared response type is a compile error here too, not silent drift.
const EstimateGrid = ({ estimate }: { estimate: Partial<import('@smartgrow/shared').YieldEstimateResponse> }) => (
  <Grid container spacing={2} sx={{ mt: 0.5 }}>
    <Grid item xs={6} sm={3}>
      <Typography variant="caption" color="text.secondary">Yield</Typography>
      <Typography fontWeight="medium">
        {estimate.yieldQuantity != null ? estimate.yieldQuantity.toLocaleString('en-NG') : '—'}
        {estimate.yieldUnit ? ` ${estimate.yieldUnit}` : ''}
      </Typography>
    </Grid>
    <Grid item xs={6} sm={3}>
      <Typography variant="caption" color="text.secondary">Price per Unit</Typography>
      <Typography fontWeight="medium">{fmt(estimate.pricePerUnit)}</Typography>
    </Grid>
    <Grid item xs={6} sm={3}>
      <Typography variant="caption" color="text.secondary">Final Cost</Typography>
      <Typography fontWeight="medium">{fmt(estimate.finalCost)}</Typography>
    </Grid>
    <Grid item xs={6} sm={3}>
      <Typography variant="caption" color="text.secondary">Net Revenue</Typography>
      <Typography fontWeight="medium" color={estimate.netRevenue == null ? 'text.primary' : estimate.netRevenue >= 0 ? 'success.main' : 'error.main'}>
        {fmt(estimate.netRevenue)}
      </Typography>
    </Grid>
    {(estimate.cultivationCost != null || estimate.harvestingCost != null || estimate.baggingCost != null || estimate.transportationCost != null) && (
      <Grid item xs={12}>
        <Typography variant="caption" color="text.secondary">
          Cost breakdown — Cultivation: {fmt(estimate.cultivationCost)}
          {' · '}Harvesting: {fmt(estimate.harvestingCost)}
          {' · '}Bagging: {fmt(estimate.baggingCost)}
          {' · '}Transportation: {fmt(estimate.transportationCost)}
        </Typography>
      </Grid>
    )}
  </Grid>
);

const YieldEstimateCard = ({ operation, onApplied }: { operation: FarmOperation; onApplied: () => void }) => {
  const [yieldEstimate, setYieldEstimate] = useState<import('@smartgrow/shared').YieldEstimateResponse | null>(null);
  const [yieldEstimateError, setYieldEstimateError] = useState('');
  const [priceSuggestion, setPriceSuggestion] = useState<import('@smartgrow/shared').PriceSuggestionResponse | null>(null);
  const [priceSuggestionError, setPriceSuggestionError] = useState('');
  const [chatMessages, setChatMessages] = useState<import('@smartgrow/shared').EstimateChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatError, setChatError] = useState('');
  const [chatUpdatedEstimate, setChatUpdatedEstimate] = useState<import('@smartgrow/shared').YieldEstimateResponse | null>(null);

  const estimateYieldMutation = useMutation({
    mutationFn: () => aiAdvisorService.estimateYield(operation.id),
    onSuccess: (res) => { setYieldEstimate(res); setYieldEstimateError(''); },
    onError: () => { setYieldEstimate(null); setYieldEstimateError('Failed to get a yield estimate. Try again.'); },
  });

  const suggestPriceMutation = useMutation({
    mutationFn: () => aiAdvisorService.suggestPrice({
      cropVariety: operation.cropVariety || operation.name || '',
      unitLabel: operation.yieldUnit ? YIELD_UNIT_LABELS[operation.yieldUnit] : undefined,
    }),
    onSuccess: (res) => { setPriceSuggestion(res); setPriceSuggestionError(''); },
    onError: () => { setPriceSuggestion(null); setPriceSuggestionError('Failed to get a price suggestion. Try again.'); },
  });

  const applyPriceMutation = useMutation({
    mutationFn: (price: number) => farmOperationService.update(operation.id, buildEstimateApplyPayload(operation, { pricePerUnit: price })),
    onSuccess: () => { onApplied(); setPriceSuggestion(null); },
    onError: () => setPriceSuggestionError('Failed to apply the price. Try again.'),
  });

  const chatMutation = useMutation({
    mutationFn: (messages: import('@smartgrow/shared').EstimateChatMessage[]) =>
      aiAdvisorService.chatAboutEstimate(operation.id, { messages }),
    onSuccess: (res) => {
      setChatMessages((prev) => [...prev, { role: 'assistant', content: res.reply }]);
      setChatUpdatedEstimate(res.updatedEstimate ?? null);
      setChatError('');
    },
    onError: () => setChatError('Failed to get a reply. Try again.'),
  });

  const applyMutation = useMutation({
    mutationFn: (estimate: import('@smartgrow/shared').YieldEstimateResponse) =>
      farmOperationService.update(operation.id, buildEstimateApplyPayload(operation, estimate)),
    onSuccess: () => { onApplied(); setYieldEstimate(null); setChatUpdatedEstimate(null); },
    onError: () => setYieldEstimateError('Failed to apply the estimate. Try again.'),
  });

  const sendChatMessage = () => {
    const text = chatInput.trim();
    if (!text || chatMutation.isPending) return;
    const next = [...chatMessages, { role: 'user' as const, content: text }];
    setChatMessages(next);
    setChatInput('');
    chatMutation.mutate(next);
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <AutoAwesome color="primary" />
        <Typography variant="h6">Yield &amp; Revenue Estimate</Typography>
        <Typography variant="caption" color="text.secondary">— AI-reasoned from recorded tasks, inputs, rain log, and location</Typography>
      </Box>
      <Stack direction="row" spacing={1.5} flexWrap="wrap">
        <Button
          variant="outlined" startIcon={suggestPriceMutation.isPending ? <CircularProgress size={14} /> : <AutoAwesome />}
          disabled={!operation.cropVariety || suggestPriceMutation.isPending}
          onClick={() => { setPriceSuggestion(null); setPriceSuggestionError(''); suggestPriceMutation.mutate(); }}
        >
          Suggest price (AI estimate)
        </Button>
        <Button
          variant="outlined" startIcon={estimateYieldMutation.isPending ? <CircularProgress size={14} /> : <AutoAwesome />}
          disabled={estimateYieldMutation.isPending}
          onClick={() => { setYieldEstimate(null); setYieldEstimateError(''); estimateYieldMutation.mutate(); }}
        >
          Estimate yield &amp; revenue (AI estimate)
        </Button>
      </Stack>

      {priceSuggestionError && (
        <Alert severity="warning" sx={{ mt: 1.5 }} onClose={() => setPriceSuggestionError('')}>{priceSuggestionError}</Alert>
      )}

      {priceSuggestion && !priceSuggestion.error && (
        <EstimateGrid estimate={{ pricePerUnit: priceSuggestion.pricePerUnit }} />
      )}

      {priceSuggestion && (
        <Alert
          severity={priceSuggestion.error ? 'warning' : 'info'} sx={{ mt: 1.5 }} onClose={() => setPriceSuggestion(null)}
          action={!priceSuggestion.error && priceSuggestion.pricePerUnit != null && (
            <Button color="inherit" size="small" disabled={applyPriceMutation.isPending} onClick={() => applyPriceMutation.mutate(priceSuggestion.pricePerUnit!)}>
              {applyPriceMutation.isPending ? <CircularProgress size={14} /> : 'Apply'}
            </Button>
          )}
        >
          {priceSuggestion.reasoning}
        </Alert>
      )}

      {yieldEstimateError && (
        <Alert severity="warning" sx={{ mt: 1.5 }} onClose={() => setYieldEstimateError('')}>{yieldEstimateError}</Alert>
      )}

      {yieldEstimate && !yieldEstimate.error && <EstimateGrid estimate={yieldEstimate} />}

      {yieldEstimate && (
        <Alert
          severity={yieldEstimate.error ? 'warning' : 'info'} sx={{ mt: 1.5 }} onClose={() => setYieldEstimate(null)}
          action={!yieldEstimate.error && (yieldEstimate.yieldQuantity != null || yieldEstimate.pricePerUnit != null) && (
            <Button color="inherit" size="small" disabled={applyMutation.isPending} onClick={() => applyMutation.mutate(yieldEstimate)}>
              {applyMutation.isPending ? <CircularProgress size={14} /> : 'Apply'}
            </Button>
          )}
        >
          {yieldEstimate.reasoning}
          {yieldEstimate.actualCost != null && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.8 }}>
              Cultivation cost floored to ₦{yieldEstimate.actualCost.toLocaleString('en-NG')} actual recorded spend (tasks + payments + expenses).
            </Typography>
          )}
        </Alert>
      )}

      {(yieldEstimate || chatMessages.length > 0) && (
        <Box sx={{ mt: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: chatMessages.length ? 1 : 0 }}>
            <Chat fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              Ask about this estimate — e.g. "is this conservative?" or "what if I add fertilizer?"
            </Typography>
          </Stack>
          {chatMessages.length > 0 && (
            <Stack spacing={1} sx={{ mb: 1.5, maxHeight: 260, overflowY: 'auto' }}>
              {chatMessages.map((m, i) => (
                <Box key={i} sx={{
                  alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                  bgcolor: m.role === 'user' ? 'primary.main' : 'action.hover',
                  color: m.role === 'user' ? 'primary.contrastText' : 'text.primary',
                  px: 1.5, py: 0.75, borderRadius: 2, maxWidth: '85%',
                }}>
                  <Typography variant="body2">{m.content}</Typography>
                </Box>
              ))}
              {chatMutation.isPending && (
                <Box sx={{ alignSelf: 'flex-start' }}><CircularProgress size={16} /></Box>
              )}
            </Stack>
          )}
          {chatError && (
            <Alert severity="warning" sx={{ mb: 1.5 }} onClose={() => setChatError('')}>{chatError}</Alert>
          )}
          {chatUpdatedEstimate && (
            <Alert
              severity="success" sx={{ mb: 1.5 }} onClose={() => setChatUpdatedEstimate(null)}
              action={
                <Button color="inherit" size="small" disabled={applyMutation.isPending} onClick={() => applyMutation.mutate(chatUpdatedEstimate)}>
                  {applyMutation.isPending ? <CircularProgress size={14} /> : 'Apply'}
                </Button>
              }
            >
              The AI proposed updated numbers based on that answer — Apply to use them.
            </Alert>
          )}
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth size="small" placeholder="Ask a follow-up..."
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(); } }}
              disabled={chatMutation.isPending}
            />
            <IconButton color="primary" onClick={sendChatMessage} disabled={!chatInput.trim() || chatMutation.isPending}>
              <Send fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
      )}
    </Paper>
  );
};

const SalesPlannerCard = ({ operation }: { operation: FarmOperation }) => {
  const [targetMonth, setTargetMonth] = useState('');
  const [sellingLocation, setSellingLocation] = useState('');
  const [plan, setPlan] = useState<import('@smartgrow/shared').SalesPlanResponse | null>(null);
  const [planError, setPlanError] = useState('');

  const planMutation = useMutation({
    mutationFn: () => aiAdvisorService.buildSalesPlan({
      farmOperationId: operation.id,
      targetMonth: targetMonth || undefined,
      sellingLocation: sellingLocation || undefined,
    }),
    onSuccess: (res) => { setPlan(res); setPlanError(''); },
    onError: () => { setPlan(null); setPlanError('Failed to build a sales plan. Try again.'); },
  });

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <TrendingUp color="primary" />
        <Typography variant="h6">Sales Planner</Typography>
        <Typography variant="caption" color="text.secondary">— AI-reasoned, not a live market quote</Typography>
      </Box>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth size="small" type="month" label="Target Sale Month (optional)"
            value={targetMonth} onChange={(e) => setTargetMonth(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth size="small" label="Selling Location (optional)"
            placeholder={operation.projectName ? `Defaults to project location` : 'e.g. Kaduna, Lagos'}
            value={sellingLocation} onChange={(e) => setSellingLocation(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Button
            fullWidth variant="outlined" startIcon={planMutation.isPending ? <CircularProgress size={14} /> : <AutoAwesome />}
            disabled={planMutation.isPending}
            onClick={() => { setPlan(null); setPlanError(''); planMutation.mutate(); }}
          >
            Build sales plan
          </Button>
        </Grid>
      </Grid>

      {planError && <Alert severity="warning" sx={{ mt: 2 }} onClose={() => setPlanError('')}>{planError}</Alert>}

      {plan && (
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Baseline (current plan)</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Yield</Typography>
                  <Typography variant="body2">{plan.baselineYieldQuantity.toLocaleString()} {plan.baselineYieldUnit}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Price/unit</Typography>
                  <Typography variant="body2">₦{plan.baselinePricePerUnit.toLocaleString()}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Revenue</Typography>
                  <Typography variant="body2">{fmt(plan.baselineRevenue)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Cost</Typography>
                  <Typography variant="body2">{fmt(plan.baselineCost)}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" fontWeight="bold">Profit</Typography>
                  <Typography variant="body2" fontWeight="bold" color={plan.baselineProfit >= 0 ? 'success.main' : 'error.main'}>{fmt(plan.baselineProfit)}</Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Projected {targetMonth ? `at ${targetMonth}` : ''}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Yield</Typography>
                  <Typography variant="body2">{(plan.projectedYieldQuantity ?? plan.baselineYieldQuantity).toLocaleString()} {plan.baselineYieldUnit}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Price/unit</Typography>
                  <Typography variant="body2">₦{(plan.projectedPricePerUnit ?? plan.baselinePricePerUnit).toLocaleString()}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Revenue</Typography>
                  <Typography variant="body2" fontWeight="bold">{fmt(plan.projectedRevenue ?? plan.baselineRevenue)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Cost</Typography>
                  <Typography variant="body2">{fmt(plan.baselineCost)}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" fontWeight="bold">Profit</Typography>
                  <Typography variant="body2" fontWeight="bold" color={(plan.projectedProfit ?? plan.baselineProfit) >= 0 ? 'success.main' : 'error.main'}>
                    {fmt(plan.projectedProfit ?? plan.baselineProfit)}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {plan.recommendedSellingMonths.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Recommended selling months:</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {plan.recommendedSellingMonths.map((m) => (
                  <Chip key={m} label={m} color="success" size="small" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}

          <Alert severity={plan.error ? 'warning' : 'info'} icon={<AutoAwesome fontSize="small" />} sx={{ mt: 2 }}>{plan.reasoning}</Alert>
        </Box>
      )}
    </Paper>
  );
};

const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-NG') : '—';

const statusColor = (status: string): 'default' | 'success' | 'warning' | 'error' | 'info' => {
  if (status === 'COMPLETED' || status === 'APPROVED' || status === 'PAID') return 'success';
  if (status === 'PENDING') return 'warning';
  if (status === 'REJECTED' || status === 'CANCELLED') return 'error';
  return 'default';
};

const OperationFinancialsTab = ({ operationId }: { operationId: string }) => {
  const navigate = useNavigate();

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['farm-financial-summary', 'operation', operationId],
    queryFn: () => reportService.getFarmFinancialSummary({ operationId }),
  });

  const { data: paymentsData } = useQuery({
    queryKey: ['payments-by-operation', operationId],
    queryFn: () => paymentService.getByFarmOperation(operationId, { size: 20 }),
  });

  const { data: expensesData } = useQuery({
    queryKey: ['expenses-by-operation', operationId],
    queryFn: () => expenseService.getByFarmOperation(operationId, 0, 20),
  });

  const { data: paymentRequestsData } = useQuery({
    queryKey: ['payment-requests-by-operation', operationId],
    queryFn: () => paymentRequestService.getByFarmOperation(operationId, { size: 20 }),
  });

  const payments = paymentsData?.content ?? [];
  const expenses = expensesData?.content ?? [];
  const paymentRequests = paymentRequestsData?.content ?? [];

  if (summaryLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>;

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <Card><CardContent>
            <Typography variant="caption" color="text.secondary">Estimated Revenue</Typography>
            <Typography variant="h6" fontWeight="bold" color="success.main">{fmt(summary?.estimatedGrossRevenue)}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card><CardContent>
            <Typography variant="caption" color="text.secondary">Total Expenditure</Typography>
            <Typography variant="h6" fontWeight="bold" color="error.main">{fmt(summary?.totalExpenditure)}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card><CardContent>
            <Typography variant="caption" color="text.secondary">Net Profit</Typography>
            <Typography variant="h6" fontWeight="bold" color={(summary?.netProfit ?? 0) >= 0 ? 'success.main' : 'error.main'}>{fmt(summary?.netProfit)}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card><CardContent>
            <Typography variant="caption" color="text.secondary">ROI</Typography>
            <Typography variant="h6" fontWeight="bold" color={(summary?.roiPercent ?? 0) >= 0 ? 'success.main' : 'error.main'}>{(summary?.roiPercent ?? 0).toFixed(1)}%</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
        Scoped to this operation only — the project's own Financials tab rolls up every operation under it.
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Payments fontSize="small" />
                  <Typography variant="subtitle1" fontWeight="bold">Payments ({paymentsData?.totalElements ?? 0})</Typography>
                </Stack>
              </Stack>
              {payments.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Payments sx={{ fontSize: 40, color: 'text.disabled' }} />
                  <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>No payments</Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {payments.map((p) => (
                    <Box key={p.id} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                      onClick={() => navigate(`/dashboard/payments/${p.id}`)}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ flex: 1, mr: 1 }}>
                          <Typography variant="body2" fontWeight="bold" noWrap>
                            {p.paymentType?.replace(/_/g, ' ')} {p.contractorName ? `— ${p.contractorName}` : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {p.referenceNumber ?? p.paymentMethod} · {fmtDate(p.paymentDate)}
                          </Typography>
                        </Box>
                        <Stack alignItems="flex-end" spacing={0.5}>
                          <Chip label={p.status} color={statusColor(p.status)} size="small" />
                          <Typography variant="body2" fontWeight="bold" color="error.main">{fmt(p.amount)}</Typography>
                        </Stack>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ReceiptLong fontSize="small" />
                  <Typography variant="subtitle1" fontWeight="bold">Expense Requests ({expensesData?.totalElements ?? 0})</Typography>
                </Stack>
              </Stack>
              {expenses.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <ReceiptLong sx={{ fontSize: 40, color: 'text.disabled' }} />
                  <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>No expense requests</Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {expenses.map((exp) => (
                    <Box key={exp.id} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                      onClick={() => navigate(`/dashboard/expenses/${exp.id}`)}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ flex: 1, mr: 1 }}>
                          <Typography variant="body2" fontWeight="bold" noWrap>{exp.description}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {exp.requestNumber} · {fmtDate(exp.requestDate)}
                          </Typography>
                        </Box>
                        <Stack alignItems="flex-end" spacing={0.5}>
                          <Chip label={exp.status} color={statusColor(exp.status)} size="small" />
                          <Typography variant="body2" fontWeight="bold">{fmt(exp.amount)}</Typography>
                        </Stack>
                      </Stack>
                      {exp.categoryName && <Typography variant="caption" color="text.secondary">{exp.categoryName}</Typography>}
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <RequestPage fontSize="small" />
                  <Typography variant="subtitle1" fontWeight="bold">Payment Requests ({paymentRequestsData?.totalElements ?? 0})</Typography>
                </Stack>
              </Stack>
              {paymentRequests.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <RequestPage sx={{ fontSize: 40, color: 'text.disabled' }} />
                  <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>No payment requests</Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {paymentRequests.map((pr) => (
                    <Box key={pr.id} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ flex: 1, mr: 1 }}>
                          <Typography variant="body2" fontWeight="bold" noWrap>{pr.description}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {pr.requestNumber} · {pr.requestType?.replace(/_/g, ' ')} · {fmtDate(pr.requestDate)}
                          </Typography>
                        </Box>
                        <Stack alignItems="flex-end" spacing={0.5}>
                          <Chip label={pr.status} color={statusColor(pr.status)} size="small" />
                          <Typography variant="body2" fontWeight="bold">{fmt(pr.amount)}</Typography>
                        </Stack>
                      </Stack>
                      {pr.requestedByName && <Typography variant="caption" color="text.secondary">By {pr.requestedByName}</Typography>}
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
